import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Reply, Send, X, Trash2, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { decodeMimeWords, senderLabel } from "@/lib/mime";
import EmailHtmlView from "@/components/EmailHtmlView";

const BACKEND_URL = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getInitials(email) {
  if (!email) return "?";
  const name = email.split("@")[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function getAvatarColor(email) {
  if (!email) return "bg-zinc-700";
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    "bg-cyan-600", "bg-blue-600", "bg-violet-600", "bg-purple-600",
    "bg-pink-600", "bg-rose-600", "bg-orange-600", "bg-amber-600",
    "bg-emerald-600", "bg-teal-600", "bg-indigo-600", "bg-fuchsia-600",
  ];
  return colors[Math.abs(hash) % colors.length];
}

function formatRelativeTime(dateString, t) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffSecs < 60) return t.justNow;
  if (diffMins < 60) return t.minutesAgo(diffMins);
  if (diffHours < 24) return t.hoursAgo(diffHours);
  if (diffDays < 7) return t.daysAgo(diffDays);
  return date.toLocaleDateString(t.dateLocale, { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function MessageModal({
  isOpen, onOpenChange, message, emailId, replyEnabled,
  messages = [], fetchMessage, deleteMessage,
}) {
  const [showReply, setShowReply] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useI18n();

  const handleClose = (open) => {
    if (!open) {
      setShowReply(false);
      setReplyBody("");
    }
    onOpenChange(open);
  };

  const handleReply = () => {
    setShowReply(true);
    setReplyBody("");
  };

  const sendReply = async () => {
    if (!replyBody.trim() || !message || !emailId) return;
    setIsSending(true);
    try {
      await axios.post(`${API}/emails/${emailId}/reply`, {
        to: message.sender,
        subject: message.subject.startsWith("Re:") ? message.subject : `Re: ${message.subject}`,
        body: replyBody,
        message_id: message.id,
      });
      toast.success(t.toastReplySent);
      setShowReply(false);
      setReplyBody("");
    } catch (error) {
      const detail = error.response?.data?.detail || t.toastUnknownError;
      toast.error(t.toastReplyError, { description: detail });
    } finally {
      setIsSending(false);
    }
  };

  const handleDelete = async () => {
    if (!message?.id || !deleteMessage) return;
    setIsDeleting(true);
    await deleteMessage(message.id);
    setIsDeleting(false);
  };

  // Navigation
  const currentIndex = useMemo(() => {
    if (!message || !messages.length) return -1;
    return messages.findIndex(m => m.id === message.id);
  }, [message, messages]);

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < messages.length - 1;

  const goToPrev = () => {
    if (hasPrev && fetchMessage) {
      fetchMessage(messages[currentIndex - 1].id);
    }
  };

  const goToNext = () => {
    if (hasNext && fetchMessage) {
      fetchMessage(messages[currentIndex + 1].id);
    }
  };

  const isSent = message?.direction === "sent";
  const hasBeenReplied = message && messages.some(m => m.direction === "sent" && m.replied_to === message.id);
  const displayContact = isSent ? (message?.recipient || message?.sender) : (message ? senderLabel(message) : "");
  const initials = getInitials(displayContact);
  const avatarColor = isSent ? "bg-emerald-600" : getAvatarColor(displayContact);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      {/* [&>*]:min-w-0 : DialogContent est une grille CSS, or ses enfants ont
          min-width:auto et refusent donc de rétrécir sous la largeur de leur
          contenu (un long identifiant d'expéditeur élargissait tout le modal
          au-delà de l'écran, malgré la max-width). */}
      <DialogContent className="bg-zinc-950 border border-zinc-800 max-w-[calc(100vw-1rem)] sm:max-w-2xl overflow-hidden [&>*]:min-w-0" data-testid="message-modal">
        {/* Navigation header */}
        {messages.length > 1 && currentIndex >= 0 && (
          <div className="flex items-center justify-between pb-2 border-b border-zinc-800/50 -mt-2">
            <Button
              onClick={goToPrev}
              disabled={!hasPrev}
              variant="ghost"
              size="sm"
              className="text-zinc-500 hover:text-cyan-400 disabled:opacity-30 font-mono text-xs h-7 px-2"
              title={t.previousMessage}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">{t.previousMessage}</span>
            </Button>
            <span className="font-mono text-[10px] text-zinc-600">
              {t.messageOf(currentIndex + 1, messages.length)}
            </span>
            <Button
              onClick={goToNext}
              disabled={!hasNext}
              variant="ghost"
              size="sm"
              className="text-zinc-500 hover:text-cyan-400 disabled:opacity-30 font-mono text-xs h-7 px-2"
              title={t.nextMessage}
            >
              <span className="hidden sm:inline">{t.nextMessage}</span>
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        <DialogHeader>
          <DialogTitle className={`font-mono pr-8 break-words ${isSent ? "text-emerald-400" : "text-cyan-400"}`}>
            {decodeMimeWords(message?.subject)}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 font-mono text-xs break-all">
            {isSent ? `→ ${message?.recipient || message?.sender}` : t.messageFrom(message ? senderLabel(message) : "")}
          </DialogDescription>
        </DialogHeader>

        {message && (
          <div className="space-y-4">
            {/* Sender/recipient info with avatar + relative time */}
            <div className="flex items-center justify-between gap-2 py-3 border-b border-zinc-800">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-9 h-9 rounded-full ${avatarColor} flex items-center justify-center shrink-0`}>
                  {isSent ? <Send className="w-4 h-4 text-white" /> : <span className="text-white text-xs font-mono font-bold">{initials}</span>}
                </div>
                <div className="min-w-0">
                  <span className="text-zinc-500 text-[10px] font-mono uppercase">{isSent ? "→" : t.from}</span>
                  <p className={`font-mono text-sm break-all ${isSent ? "text-emerald-400" : "text-white"}`}>{isSent ? (message.recipient || message.sender) : senderLabel(message)}</p>
                  {!isSent && message.sender_name && (
                    <p className="text-zinc-500 font-mono text-[10px] break-all">{decodeMimeWords(message.sender)}</p>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <span className="text-zinc-500 text-[10px] font-mono uppercase">{isSent ? "Envoyé" : t.received}</span>
                <p className={`font-mono text-sm font-medium ${isSent ? "text-emerald-400" : "text-cyan-400"}`}>
                  {formatRelativeTime(message.received_at, t)}
                </p>
                <p className="text-zinc-600 font-mono text-[10px]">
                  {new Date(message.received_at).toLocaleString(t.dateLocale)}
                </p>
              </div>
            </div>

            {/* Radix pose display:table sur le conteneur interne du viewport,
                qui s'élargit alors au contenu : on le force en block pour que
                l'email reste borné à la largeur de l'écran. */}
            <ScrollArea className="h-[300px] [&>[data-radix-scroll-area-viewport]>div]:!block">
              {message.body_html ? (
                <EmailHtmlView html={message.body_html} data-testid="message-body-html" />
              ) : (
                <pre
                  className="whitespace-pre-wrap break-words text-zinc-300 font-mono text-sm"
                  data-testid="message-body-text"
                >
                  {message.body}
                </pre>
              )}
            </ScrollArea>

            {/* Action buttons */}
            <div className="border-t border-zinc-800 pt-3">
              {showReply ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono text-zinc-400">
                      {t.replyTo} <span className="text-cyan-400">{message.sender}</span>
                    </span>
                    <button
                      onClick={() => { setShowReply(false); setReplyBody(""); }}
                      className="text-zinc-500 hover:text-white transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <Input
                    value={message.subject.startsWith("Re:") ? message.subject : `Re: ${message.subject}`}
                    disabled
                    className="bg-zinc-900/50 border-zinc-700/50 text-zinc-500 text-xs h-8 font-mono cursor-not-allowed"
                  />
                  <textarea
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder={t.replyPlaceholder}
                    rows={4}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-md text-white text-sm font-mono p-3 resize-none focus:outline-none focus:ring-1 focus:ring-cyan-500/50 placeholder:text-zinc-600"
                    autoFocus
                  />
                  <Button
                    onClick={sendReply}
                    disabled={isSending || !replyBody.trim()}
                    className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-mono text-xs"
                  >
                    {isSending ? (
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
                    ) : (
                      <Send className="w-3.5 h-3.5 mr-2" />
                    )}
                    {t.send}
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  {replyEnabled && !isSent && (
                    hasBeenReplied ? (
                      <Button
                        disabled
                        variant="outline"
                        className="flex-1 border-emerald-500/20 text-emerald-400/60 font-mono text-xs cursor-not-allowed"
                      >
                        <Check className="w-3.5 h-3.5 mr-2" />
                        Répondu
                      </Button>
                    ) : (
                      <Button
                        onClick={handleReply}
                        variant="outline"
                        className="flex-1 border-zinc-700 text-zinc-400 hover:text-cyan-400 hover:border-cyan-500/30 hover:bg-cyan-500/5 font-mono text-xs"
                      >
                        <Reply className="w-3.5 h-3.5 mr-2" />
                        {t.reply}
                      </Button>
                    )
                  )}
                  <Button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    variant="outline"
                    className={`border-zinc-700 text-red-400/70 hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/5 font-mono text-xs ${replyEnabled && !isSent ? '' : 'flex-1'}`}
                  >
                    <Trash2 className={`w-3.5 h-3.5 mr-2 ${isDeleting ? 'animate-pulse' : ''}`} />
                    {t.deleteMessage}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
